import {
  AlertCircle,
  BarChart3,
  Brain,
  Clock,
  Hash,
  Image,
  Lightbulb,
  RefreshCw,
  Target,
  TrendingUp,
  Zap,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { Button, EmptyState, SectionHeader } from "../../components";
import analyticsService from "../../services/analyticsService";
import { IntelligentInsightsData } from "../../types/analytics";
import { cn } from "../../utils/cn";
import { analyticsCache } from "../../utils/smartCache";
import Card from "../ui/Card";

import IntelligentInsightsSkeleton from "./skeletons/IntelligentInsightsSkeleton";

const IntelligentInsights: React.FC = () => {
  const [insights, setInsights] = useState<IntelligentInsightsData | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trainingModels, setTrainingModels] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);

  const fetchInsights = useCallback(
    async (retryWithoutCache = false): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        if (retryWithoutCache) {
          analyticsCache.invalidateAllAnalytics();
        }

        const response = await analyticsService.getIntelligentSuggestions();

        if (response.success && response.campaign_insights) {
          setUsingFallback(false);
          const insightsData: IntelligentInsightsData = {
            campaign_insights: response.campaign_insights,
            next_post_suggestions: response.next_post_suggestions || [],
            model_status: response.model_status || {
              is_trained: true,
              training_data_size: 150,
            },
          };

          setInsights(insightsData);
        } else {
          if (!retryWithoutCache) {
            return await fetchInsights(true);
          }
          analyticsCache.invalidateAllAnalytics();
          throw new Error("Dados inválidos recebidos do servidor");
        }
      } catch (err) {
        console.error("Erro ao buscar insights:", err);
        const message =
          err instanceof Error ? err.message : "Erro desconhecido";
        setError(
          message.includes("503") || message.toLowerCase().includes("ensemble")
            ? "Serviço de ML indisponível neste ambiente."
            : message,
        );

        // Fallback data — user will see a banner indicating these are benchmarks
        setUsingFallback(true);
        setInsights({
          campaign_insights: {
            performance_analysis: {
              avg_engagement_rate: 4.2,
              best_performing_hours: { "9": 5.8, "10": 5.5, "11": 4.9 },
              best_performing_days: { "1": 4.8, "2": 4.9, "3": 5.1 },
              content_length_correlation: 0.15,
              hashtag_effectiveness: 0.08,
            },
            content_optimization: {
              optimal_content_length: { min: 100, max: 200, avg: 150 },
              optimal_hashtag_count: 3,
              call_to_action_impact: 0.12,
              image_impact: 0.25,
            },
            timing_insights: {
              peak_hours: { "9": 5.8, "10": 5.5, "11": 4.9 },
              peak_days: { "1": 4.8, "2": 4.9, "3": 5.1 },
              weekend_vs_weekday: { weekend_avg: 3.2, weekday_avg: 4.8 },
            },
            campaign_suggestions: [
              {
                type: "timing",
                title: "Otimizar Horários",
                description:
                  "Focalizar em janelas de alto engajamento identificadas.",
                action: "Programar posts para o intervalo entre 9h e 11h.",
                impact: "alto",
              },
            ],
          },
          next_post_suggestions: [
            {
              type: "content",
              title: "Impacto Visual",
              suggestion:
                "Posts com imagens geram 25% mais interações em sua conta.",
              confidence: 0.85,
              impact: "alto",
            },
          ],
          model_status: {
            is_trained: true,
            training_data_size: 150,
          },
        });
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const trainModels = async () => {
    try {
      setTrainingModels(true);
      const data = await analyticsService.trainModels();
      if (data.success) {
        await fetchInsights();
      } else {
        setError(data.message || "Erro no treinamento");
      }
    } catch (err) {
      console.error("Erro no treinamento:", err);
      const message =
        err instanceof Error ? err.message : "Erro no treinamento";
      setError(
        message.includes("503") || message.toLowerCase().includes("ensemble")
          ? "Treinamento indisponível: ML não habilitado neste ambiente."
          : message,
      );
    } finally {
      setTrainingModels(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const getImpactStyle = (impact: string) => {
    switch (impact?.toLowerCase()) {
      case "alto":
        return "bg-emerald-50 text-emerald-600 border-emerald-100";
      case "médio":
        return "bg-amber-50 text-amber-600 border-amber-100";
      default:
        return "bg-blue-50 text-blue-600 border-blue-100";
    }
  };

  if (loading) {
    return <IntelligentInsightsSkeleton />;
  }

  if (error && !insights) {
    return (
      <Card className="p-3 border-dashed border-2 border-red-100 bg-red-50/10 flex flex-col items-center justify-center text-center">
        <AlertCircle className="h-10 w-10 text-red-500 mb-2" />
        <h3 className="text-sm font-black uppercase tracking-widest text-gray-900 mb-2">
          Erro nos Insights
        </h3>
        <p className="text-xs text-gray-500 mb-2">{error}</p>
        <Button onClick={() => fetchInsights()} variant="outline" size="sm">
          Tentar Novamente
        </Button>
      </Card>
    );
  }

  if (!insights) return null;

  return (
    <div className="space-y-2 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {usingFallback && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-amber-200 bg-amber-50/60 text-amber-700">
          <AlertCircle size={15} className="shrink-0 text-amber-500" />
          <p className="text-xs font-semibold">
            Serviço de ML indisponível — exibindo{" "}
            <strong>benchmarks globais</strong>, não dados reais da sua conta.{" "}
            <button
              onClick={() => fetchInsights(true)}
              className="underline font-black hover:text-amber-900"
            >
              Tentar novamente
            </button>
          </p>
        </div>
      )}

      {/* Model Status Header — mesmo tratamento visual do header de "Perfil
          Estratégico do ICP" (EmpathyMapDisplay.tsx): SectionHeader real
          dentro do seu próprio card. */}
      <div className="bg-white border border-purple-200 rounded-xl p-3 shadow-xs">
        <SectionHeader
          icon={<Brain className="h-5 w-5" />}
          title="Núcleo de Inteligência"
          subtitle="Otimização preditiva via Machine Learning"
          hideBorder
          action={
            <div className="flex flex-wrap items-center gap-2 self-center">
              <div className="px-3 py-1.5 bg-gray-50 border border-purple-200 rounded-lg flex items-center gap-2">
                <div
                  className={`w-2 h-2 rounded-full shrink-0 ${insights.model_status.is_trained ? "bg-emerald-500 animate-pulse" : "bg-amber-500"}`}
                />
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 leading-none">
                    Status dos Modelos
                  </p>
                  <p className="text-xs font-bold text-gray-700 leading-none mt-0.5">
                    {insights.model_status.is_trained
                      ? "Altamente Treinados"
                      : "Aguardando Dados"}
                  </p>
                </div>
              </div>

              <Button
                onClick={trainModels}
                disabled={trainingModels}
                size="sm"
                className="h-9! px-4! shrink-0 bg-purple-600 hover:bg-purple-700 shadow-xs font-black uppercase text-[10px] tracking-widest"
              >
                {trainingModels ? (
                  <span className="flex items-center whitespace-nowrap">
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin shrink-0" />
                    Processando...
                  </span>
                ) : (
                  <span className="flex items-center whitespace-nowrap">
                    <Zap className="mr-2 h-4 w-4 fill-white shrink-0" />
                    Sincronizar Cérebro
                  </span>
                )}
              </Button>
            </div>
          }
        />
      </div>

      {/* Content Suggestions Grid */}
      <div className="space-y-2">
        <div className="flex items-center gap-3 px-2">
          <Lightbulb className="text-amber-500" size={20} />
          <h3 className="text-sm font-black uppercase tracking-widest text-gray-900">
            Estratégias de Próximo Post
          </h3>
        </div>

        {insights.next_post_suggestions.length === 0 ? (
          <EmptyState
            icon={<Lightbulb className="h-8 w-8 text-gray-300" />}
            title="Nenhuma estratégia disponível"
            description="A IA ainda não gerou sugestões para o próximo post neste ciclo."
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {insights.next_post_suggestions.map((suggestion, index) => (
              <Card
                key={index}
                className="p-3 border-purple-200 hover:shadow-md hover:scale-[1.02] transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-xl ${suggestion.type === "timing" ? "bg-blue-50 text-blue-600" : "bg-emerald-50 text-emerald-600"}`}
                    >
                      {suggestion.type === "timing" ? (
                        <Clock size={16} />
                      ) : (
                        <Target size={16} />
                      )}
                    </div>
                    <h4 className="text-sm font-black text-gray-800 tracking-tight">
                      {suggestion.title}
                    </h4>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${getImpactStyle(suggestion.impact)}`}
                  >
                    Impacto {suggestion.impact}
                  </span>
                </div>

                <p className="text-sm text-gray-600 leading-relaxed mb-2 italic">
                  "{suggestion.suggestion}"
                </p>

                {suggestion.confidence && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                        Nível de Confiança
                      </span>
                      <span className="text-[10px] font-black text-purple-600">
                        {(suggestion.confidence * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-gray-50 rounded-full overflow-hidden border border-purple-200">
                      <div
                        className="h-full bg-linear-to-r from-purple-400 to-purple-600 rounded-full"
                        style={{ width: `${suggestion.confidence * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Metrics Breakdown */}
      {insights.campaign_insights?.performance_analysis && (
        <div className="space-y-2">
          <div className="flex items-center gap-3 px-2">
            <BarChart3 className="text-blue-500" size={20} />
            <h3 className="text-sm font-black uppercase tracking-widest text-gray-900">
              Análise de Redes
            </h3>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {[
              {
                label: "Engajamento IA",
                val: (() => {
                  const rate =
                    insights.campaign_insights?.performance_analysis
                      ?.avg_engagement_rate;
                  if (typeof rate !== "number" || Number.isNaN(rate))
                    return "N/D";
                  return `${(rate * 100).toFixed(1)}%`;
                })(),
                icon: TrendingUp,
                color: "blue",
              },
              {
                label: "Janela de Ouro",
                val: (() => {
                  const hours =
                    insights.campaign_insights?.performance_analysis
                      ?.best_performing_hours;
                  if (!hours) return "N/D";
                  const bestHour = Object.keys(hours)[0];
                  return bestHour !== undefined ? `${bestHour}h` : "N/D";
                })(),
                icon: Clock,
                color: "emerald",
              },
              {
                label: "Peso Tags",
                val: (() => {
                  const tagEff =
                    insights.campaign_insights?.performance_analysis
                      ?.hashtag_effectiveness;
                  if (typeof tagEff !== "number" || Number.isNaN(tagEff))
                    return "N/D";
                  return `${(tagEff * 100).toFixed(0)}%`;
                })(),
                icon: Hash,
                color: "purple",
              },
              {
                label: "Correlação",
                val: (() => {
                  const corr =
                    insights.campaign_insights?.performance_analysis
                      ?.content_length_correlation;
                  if (typeof corr !== "number" || Number.isNaN(corr))
                    return "N/D";
                  return `${(corr * 100).toFixed(0)}%`;
                })(),
                icon: Zap,
                color: "amber",
              },
            ].map((stat, i) => {
              const KPI_COLORS = {
                blue: {
                  border: "border-blue-200",
                  text: "text-blue-700",
                  iconBg: "bg-blue-100",
                  iconText: "text-blue-600",
                },
                emerald: {
                  border: "border-emerald-200",
                  text: "text-emerald-700",
                  iconBg: "bg-emerald-100",
                  iconText: "text-emerald-600",
                },
                purple: {
                  border: "border-purple-200",
                  text: "text-purple-700",
                  iconBg: "bg-purple-100",
                  iconText: "text-purple-600",
                },
                amber: {
                  border: "border-amber-200",
                  text: "text-amber-700",
                  iconBg: "bg-amber-100",
                  iconText: "text-amber-600",
                },
              };
              const s = KPI_COLORS[stat.color as keyof typeof KPI_COLORS];
              return (
                <div
                  key={i}
                  className={cn(
                    "relative bg-white rounded-xl border p-4 shadow-xs transition-all duration-200 overflow-hidden hover:-translate-y-0.5",
                    s.border,
                  )}
                >
                  <div
                    className={cn(
                      "absolute top-0 left-0 right-0 h-[3px]",
                      s.iconBg,
                    )}
                  />
                  <div className="flex items-start justify-between pt-0.5">
                    <div className="flex flex-col min-w-0">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 truncate">
                        {stat.label}
                      </p>
                      <h3
                        className={cn(
                          "text-2xl font-black tracking-tight leading-none tabular-nums",
                          s.text,
                        )}
                      >
                        {stat.val}
                      </h3>
                    </div>
                    <div
                      className={cn(
                        "w-9 h-9 rounded-lg flex items-center justify-center shrink-0",
                        s.iconBg,
                        s.iconText,
                      )}
                    >
                      <stat.icon className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Advanced Optimization Card */}
      {insights.campaign_insights?.content_optimization && (
        <Card className="p-3 border-purple-200 bg-linear-to-br from-white to-purple-50/50 shadow-xs">
          <SectionHeader
            icon={<Zap className="h-5 w-5" />}
            title="Otimização Cirúrgica"
            subtitle="Parâmetros ideais de conteúdo para maximizar conversão"
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            <div className="space-y-2">
              <h4 className="text-xs font-black uppercase tracking-widest text-purple-900/60">
                Anatomia do Texto
              </h4>
              <div className="space-y-2">
                {[
                  {
                    label: "Mínimo Aceitável",
                    val:
                      insights.campaign_insights.content_optimization
                        ?.optimal_content_length?.min ?? "N/D",
                    type: "char",
                  },
                  {
                    label: "Limite Seguro",
                    val:
                      insights.campaign_insights.content_optimization
                        ?.optimal_content_length?.max ?? "N/D",
                    type: "char",
                  },
                  {
                    label: "Tamanho Ideal (Sweet Spot)",
                    val:
                      insights.campaign_insights.content_optimization
                        ?.optimal_content_length?.avg ?? "N/D",
                    type: "highlight",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex justify-between items-center py-2 border-b border-purple-200"
                  >
                    <span className="text-xs font-semibold text-gray-500">
                      {item.label}
                    </span>
                    <span
                      className={`text-sm font-black ${item.type === "highlight" ? "text-purple-600" : "text-gray-900"}`}
                    >
                      {item.val} {typeof item.val === "number" ? "chars" : ""}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-xs font-black uppercase tracking-widest text-purple-900/60">
                Fatores de Impulso
              </h4>
              <div className="space-y-2">
                {[
                  {
                    label: "Hashtags Estratégicas",
                    val:
                      insights.campaign_insights.content_optimization
                        ?.optimal_hashtag_count ?? "N/D",
                    icon: Hash,
                    color: "purple",
                  },
                  {
                    label: "Call-to-Action Impact",
                    val: (() => {
                      const val =
                        insights.campaign_insights.content_optimization
                          ?.call_to_action_impact;
                      if (typeof val !== "number" || Number.isNaN(val))
                        return "N/D";
                      return `+${(val * 100).toFixed(1)}%`;
                    })(),
                    icon: Target,
                    color: "amber",
                  },
                  {
                    label: "Assets Visuais",
                    val: (() => {
                      const val =
                        insights.campaign_insights.content_optimization
                          ?.image_impact;
                      if (typeof val !== "number" || Number.isNaN(val))
                        return "N/D";
                      return `+${(val * 100).toFixed(1)}%`;
                    })(),
                    icon: Image,
                    color: "blue",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-2.5 bg-gray-50/50 border border-purple-200 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <item.icon
                        size={16}
                        className={`text-${item.color}-500`}
                      />
                      <span className="text-xs font-bold text-gray-700">
                        {item.label}
                      </span>
                    </div>
                    <span
                      className={`text-sm font-black text-${item.color}-600`}
                    >
                      {item.val}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default IntelligentInsights;
